import { CompanyProvider } from '@/features/_contexts/company-context'
import { queries } from '@/features/_lib/react-query'
import { useCompanyStore, useFacilityStore } from '@/features/_stores'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Outlet, createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const Route = createFileRoute('/_authed/companies/$companySlug')({
  component: CompanyLayout,
})

function CompanyLayout() {
  const { companySlug } = Route.useParams()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const isUUID = UUID_REGEX.test(companySlug)

  // Resolve slug → company
  const { data: company } = useQuery({
    ...queries.companies.bySlug(companySlug),
    enabled: !isUUID,
  })

  // Fallback: if param is a UUID, fetch by ID and redirect to slug
  const { data: companyById } = useQuery({
    ...queries.companies.detail(companySlug),
    enabled: isUUID,
  })

  useEffect(() => {
    if (isUUID && companyById) {
      const companyPrefix = `/companies/${companySlug}`
      const suffix = pathname.startsWith(companyPrefix) ? pathname.slice(companyPrefix.length) : ''
      navigate({
        to: `/companies/${companyById.slug}${suffix}` as any,
        replace: true,
      })
    }
  }, [isUUID, companyById, companySlug, pathname, navigate])

  // Sync facilities to store
  const resolvedCompany = isUUID ? undefined : company

  const { data: facilities } = useQuery({
    ...queries.companies.facilities(resolvedCompany?.id ?? ''),
    enabled: !!resolvedCompany,
  })

  const queryClient = useQueryClient()

  // Sync company and facilities to store on company change
  useEffect(() => {
    if (!resolvedCompany) return
    const prevCompany = useCompanyStore.getState().company
    if (prevCompany?.id !== resolvedCompany.id) {
      useFacilityStore.getState().clear()
      queryClient.removeQueries({ queryKey: queries.applications._def })
      queryClient.removeQueries({ queryKey: queries.companyApplications._def })
      queryClient.removeQueries({ queryKey: queries.persons._def })
    }
    useCompanyStore.getState().setCompany({
      id: resolvedCompany.id,
      slug: resolvedCompany.slug,
      name: resolvedCompany.name,
      cnpj: resolvedCompany.cnpj,
      legalName: resolvedCompany.legalName,
    })
  }, [resolvedCompany, queryClient])

  useEffect(() => {
    if (!facilities || !resolvedCompany) return
    const { setAvailableFacilities, setCurrentFacility, clear } = useFacilityStore.getState()
    setAvailableFacilities(facilities)
    const currentFacility = useFacilityStore.getState().currentFacility
    const isCurrentValid = currentFacility && facilities.some((f) => f.id === currentFacility.id)
    if (!isCurrentValid) {
      const first = facilities[0]
      if (first) {
        setCurrentFacility(first)
      } else {
        clear()
      }
    }
  }, [facilities, resolvedCompany])

  if (isUUID) return null // Redirecting...

  return (
    <CompanyProvider value={resolvedCompany ?? null}>
      <Outlet />
    </CompanyProvider>
  )
}
